// Part groups (migration 039): parts that are one thing in several colours or models (GCU2's box sleeves, shells,
// gateplates, caps), grouped. Every member stays its own part (SKU, stock, PO lines, spec, revision letters); the group
// only names them, shows them together and copies a shared file (the die cut) to each. Shared by the screens
// (Parts library, the item panel's Group section, the BOM editor's Fill from group) and the server (the rules in
// server/src/mutations/part-groups.ts), so both agree.
//
// - A member's values describe the part in its own words: a cap's resin colour ("Black"), a sleeve's colourway
//   ("Ghost Black"), keyed by the group's axes (varies_by). Which member a product variant gets stays in bom_option_map,
//   frozen with each BOM revision: nothing that resolves a BOM reads groups. knownAs() shows that mapping beside a member.
// - Values are unique among current members only (not archived, lifecycle npi or active), so a new SKU can replace an
//   end-of-life one of the same colour.
// - Everything else is "same by default": copied when a member is added, shown as a difference when members differ
//   (groupDiffs), never refused, because a special edition can really differ. Only kind and part type must be equal.
import {
  type BomIndex,
  type BomLineRow,
  type BomRow,
  conditionsOf,
  currentRevision,
  lineApplies,
  ownerLabel,
  productOptions,
  variantOptions,
  variantsOf,
} from "./boms.ts";
import type { Item, PartGroup, VendorItem } from "./db.gen.ts";
import type { SpecFileKind } from "./specs.ts";

export type GroupRow = Pick<PartGroup, "id" | "name" | "sku_prefix" | "varies_by" | "shared_files" | "notes">;
export type GroupValues = Record<string, string>;
export type MemberItem = Pick<Item, "id" | "sku" | "name" | "part_group_id" | "group_values" | "archived" | "lifecycle">;

export const MAX_AXES = 3;
/** The axes a new group offers first: what HHL's parts vary by. */
export const AXIS_SUGGESTIONS = ["Color", "Connection type", "Model", "Size", "Length"] as const;
/** The spec file kinds a group can share (the ones a die or a mould makes the same for every member), short names first. */
export const SHAREABLE_KINDS: readonly SpecFileKind[] = ["dieline", "step", "drawing", "datasheet", "fixture", "other"];
export const SHARED_FILE_LABELS: Record<SpecFileKind, string> = {
  dieline: "Dieline",
  step: "STEP",
  drawing: "Drawing",
  datasheet: "Datasheet",
  fixture: "Test fixture",
  other: "Other file",
  artwork: "Artwork",
  pcb_fab: "Gerbers",
  pcba: "BOM + placement",
  kicad: "KiCad project",
  design: "Design files",
  firmware: "Firmware",
};

const norm = (s: unknown) =>
  String(s ?? "")
    .trim()
    .toLowerCase();
const bySku = (a: { sku: string }, b: { sku: string }) => a.sku.localeCompare(b.sku, undefined, { numeric: true, sensitivity: "base" });
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** A member's values as stored (jsonb), as plain strings. */
export function valuesOf(item: Pick<Item, "group_values">): GroupValues {
  const raw = item.group_values;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  return Object.fromEntries(Object.entries(raw as Record<string, unknown>).map(([k, v]) => [k, String(v ?? "")]));
}

/** A member that counts: not archived, and new or active (an eol or obsolete one may have a successor of its colour). */
export const isCurrent = (i: Pick<Item, "archived" | "lifecycle">) => !i.archived && (i.lifecycle === "npi" || i.lifecycle === "active");

/** "Wireless · Atomic Purple": a member's values in its group's axis order. */
export function memberLabel(group: Pick<GroupRow, "varies_by">, item: Pick<Item, "group_values">): string {
  const values = valuesOf(item);
  return group.varies_by
    .map((a) => values[a] ?? "")
    .filter(Boolean)
    .join(" · ");
}

/** "Connection type × Color" */
export const axesText = (group: Pick<GroupRow, "varies_by">) => group.varies_by.join(" × ");

/** One key per combination of values, case and spacing aside. */
export const valuesKey = (axes: readonly string[], values: GroupValues) => axes.map((a) => norm(values[a])).join("\u0001");

/** A group's members, current ones first, each axis in the order its values first appear (by SKU). */
export function membersOf<T extends MemberItem>(items: readonly T[], groupId: string): T[] {
  return items.filter((i) => i.part_group_id === groupId).sort((a, b) => Number(isCurrent(b)) - Number(isCurrent(a)) || bySku(a, b));
}

/** Each axis's values in the order they first appear among the members (the grid's rows and columns). */
export function axisValues(group: Pick<GroupRow, "varies_by">, members: readonly MemberItem[]): string[][] {
  return group.varies_by.map((axis) => {
    const seen = new Map<string, string>();
    for (const m of [...members].sort(bySku)) {
      const v = valuesOf(m)[axis];
      if (v && !seen.has(norm(v))) seen.set(norm(v), v);
    }
    return [...seen.values()];
  });
}

/**
 * What's wrong with members' values (none: []): each needs a value for every axis and nothing else, and no two current
 * members may have the same values.
 */
export function valueProblems(group: Pick<GroupRow, "varies_by" | "name">, members: readonly MemberItem[]): string[] {
  const out: string[] = [];
  const axes = group.varies_by;
  for (const m of members) {
    const values = valuesOf(m);
    const name = m.sku || m.name;
    const missing = axes.filter((a) => !values[a]?.trim());
    const extra = Object.keys(values).filter((k) => !axes.includes(k));
    if (missing.length) out.push(`${name} needs a ${missing.join(" and ")}`);
    if (extra.length) out.push(`${name} has ${extra.join(", ")}, which ${group.name} doesn't vary by`);
  }
  const seen = new Map<string, MemberItem>();
  for (const m of members.filter(isCurrent)) {
    const key = valuesKey(axes, valuesOf(m));
    const other = seen.get(key);
    if (other) out.push(`${other.sku || other.name} and ${m.sku || m.name} are both ${memberLabel(group, other)}: change one, or mark the old one end of life`);
    else seen.set(key, m);
  }
  return out;
}

// Same on every part ---------------------------------------------------------------------------------

/**
 * The part fields members share unless one really differs (copied to a new member; "make all like this one"). Never a
 * project: a part is in the projects whose BOMs use it.
 */
export const SAME_FIELDS = [
  ["uom", "Unit"],
  ["procurement", "Procurement"],
  ["pack_type", "Category"],
  ["pack_print", "Printing"],
  ["label_w_mm", "Label width"],
  ["label_h_mm", "Label height"],
  ["label_note", "Label line"],
  ["hs_code", "HS code"],
  ["country_of_origin", "Country of origin"],
  ["weight_g", "Weight"],
] as const;
/** Fields every member must have alike (planning, consignment and the shared files read them per part). */
export const LOCKED_FIELDS = ["kind", "part_type"] as const;

export type FieldDiff = { field: string; label: string; values: { value: unknown; ids: string[] }[] };

const stable = (v: unknown) => (v == null || v === "" ? "" : typeof v === "number" ? String(Number(v)) : JSON.stringify(v));

/** Where current members differ (most common value first); [] when they're all alike. */
export function groupDiffs<T extends MemberItem>(
  members: readonly T[],
  fields: readonly (readonly [string, string])[] = SAME_FIELDS,
  get: (m: T, field: string) => unknown = (m, f) => (m as Record<string, unknown>)[f],
): FieldDiff[] {
  const current = members.filter(isCurrent);
  const out: FieldDiff[] = [];
  for (const [field, label] of fields) {
    const by = new Map<string, { value: unknown; ids: string[] }>();
    for (const m of current) {
      const value = get(m, field);
      const k = stable(value);
      const entry = by.get(k);
      entry ? entry.ids.push(m.id) : by.set(k, { value, ids: [m.id] });
    }
    if (by.size > 1) out.push({ field, label, values: [...by.values()].sort((a, b) => b.ids.length - a.ids.length) });
  }
  return out;
}

/** Vendor terms that differ between current members (their preferred link's vendor, price, MOQ, multiple, lead time). */
export function linkDiffs(
  members: readonly MemberItem[],
  links: readonly Pick<VendorItem, "item_id" | "vendor_id" | "unit_price" | "currency" | "moq" | "order_multiple" | "lead_time_days" | "is_preferred">[],
): FieldDiff[] {
  const preferred = new Map<string, (typeof links)[number]>();
  for (const l of links) if (l.is_preferred || !preferred.has(l.item_id)) preferred.set(l.item_id, l);
  const fields = [
    ["vendor_id", "Vendor"],
    ["unit_price", "Price"],
    ["moq", "MOQ"],
    ["order_multiple", "Order multiple"],
    ["lead_time_days", "Lead time"],
  ] as const;
  return groupDiffs(members, fields, (m, f) => {
    const l = preferred.get(m.id);
    if (!l) return null;
    return f === "unit_price" ? (l.unit_price == null ? null : `${Number(l.unit_price)} ${l.currency}`) : (l as Record<string, unknown>)[f];
  });
}

// Shared files -----------------------------------------------------------------------------------------

/** Where one member's spec stands on one kind of file: its draft's current upload, and its released revision's. */
export type MemberFile = { draft: string | null; released: string | null };
export type SharedFileState = {
  kind: SpecFileKind;
  label: string;
  /** Current members. */
  total: number;
  /** The file most members carry (sha256), and how many do. */
  sha: string | null;
  same: number;
  /** Members carrying another file, and members with none of that kind in their draft. */
  differs: string[];
  missing: string[];
  /** Members whose released revision has the common file. */
  released: number;
};

/**
 * Each shared kind across the group's current members, by sha256 of their draft's upload of that kind: the same file on
 * all of them, some differing (a special edition with its own cut: information, never a gap) or missing.
 */
export function sharedFileStates(
  group: Pick<GroupRow, "shared_files">,
  members: readonly MemberItem[],
  fileOf: (itemId: string, kind: SpecFileKind) => MemberFile,
): SharedFileState[] {
  const current = members.filter(isCurrent);
  return (group.shared_files as SpecFileKind[]).map((kind) => {
    const files = current.map((m) => ({ id: m.id, ...fileOf(m.id, kind) }));
    const count = new Map<string, number>();
    for (const f of files) if (f.draft) count.set(f.draft, (count.get(f.draft) ?? 0) + 1);
    const [sha, same] = [...count].sort((a, b) => b[1] - a[1])[0] ?? [null, 0];
    return {
      kind,
      label: SHARED_FILE_LABELS[kind],
      total: current.length,
      sha,
      same,
      differs: files.filter((f) => f.draft && f.draft !== sha).map((f) => f.id),
      missing: files.filter((f) => !f.draft).map((f) => f.id),
      released: sha ? files.filter((f) => f.released === sha).length : 0,
    };
  });
}

/** "Dieline same on 11 · released on 9", "Dieline on 8 of 11 · 2 differ". */
export function sharedFileText(s: SharedFileState): string {
  if (!s.total) return `${s.label}: no parts`;
  if (!s.sha) return `${s.label} missing on all ${s.total}`;
  const head = s.same === s.total ? `${s.label} same on ${s.total === 2 ? "both" : `all ${s.total}`}` : `${s.label} on ${s.same} of ${s.total}`;
  const parts = [head];
  if (s.differs.length) parts.push(`${s.differs.length} ${s.differs.length === 1 ? "differs" : "differ"}`);
  if (s.missing.length && s.same !== s.total) parts.push(`${s.missing.length} missing`);
  parts.push(s.released === s.total ? "released" : `released on ${s.released}`);
  return parts.join(" · ");
}

// New members ------------------------------------------------------------------------------------------

/** What members' SKUs start with: the group's prefix, else the SKUs' common start up to its last dash. */
export function skuStem(group: Pick<GroupRow, "sku_prefix">, members: readonly Pick<Item, "sku">[]): string {
  if (group.sku_prefix) return group.sku_prefix;
  const skus = members.map((m) => m.sku).filter(Boolean);
  if (!skus.length) return "";
  let stem = skus[0];
  for (const s of skus) while (!s.startsWith(stem)) stem = stem.slice(0, -1);
  return stem.slice(0, stem.lastIndexOf("-") + 1);
}

const initials = (text: string) => {
  const words = text
    .replace(/[^A-Za-z0-9 ]+/g, " ")
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return "";
  return (words.length === 1 ? words[0].slice(0, 2) : words.map((w) => w[0]).join("")).toUpperCase();
};

/**
 * A new member's SKU: the next number when members are numbered, counting on from the members that share its other
 * values (a Wireless sleeve after GCU2-SLV-1304 → 1305, or the next free one), else the stem and the initials of its last
 * axis's value (GCU2-CAPS- + "Ghost White" → GCU2-CAPS-GW); never one already taken.
 */
export function proposeSku(
  group: Pick<GroupRow, "sku_prefix" | "varies_by">,
  members: readonly Pick<Item, "sku" | "group_values">[],
  values: GroupValues,
  taken: (sku: string) => boolean = () => false,
): string {
  const stem = skuStem(group, members);
  const tail = (m: Pick<Item, "sku">) => (m.sku.startsWith(stem) ? m.sku.slice(stem.length) : "");
  const used = (sku: string) => taken(sku) || members.some((m) => m.sku.toUpperCase() === sku.toUpperCase());
  let sku: string;
  if (members.length && members.every((m) => /^\d+$/.test(tail(m)))) {
    const others = group.varies_by.slice(0, -1);
    const series = members.filter((m) => others.every((a) => norm(valuesOf(m)[a]) === norm(values[a])));
    const from = series.length ? series : members;
    const width = Math.max(...members.map((m) => tail(m).length));
    let n = Math.max(...from.map((m) => Number(tail(m)))) + 1;
    for (sku = `${stem}${String(n).padStart(width, "0")}`; used(sku); ) sku = `${stem}${String(++n).padStart(width, "0")}`;
    return sku;
  }
  // A successor of a colour takes its code (GCU2-CAPS-BK → BK2); a new colour its initials.
  const last = group.varies_by[group.varies_by.length - 1];
  const twin = members.find((m) => norm(valuesOf(m)[last]) === norm(values[last]) && tail(m));
  const code = (twin && tail(twin)) || initials(values[last] ?? "") || "NEW";
  sku = `${stem}${code}`;
  for (let n = 2; used(sku); n++) sku = `${stem}${code}${n}`;
  return sku;
}

/** A new member's name: a sibling's with its values swapped for the new ones ("… - Trans Black" → "… - Ghost White"). */
export function proposeName(group: Pick<GroupRow, "varies_by">, from: Pick<Item, "name" | "group_values">, values: GroupValues): string {
  const old = valuesOf(from);
  let name = from.name;
  for (const axis of group.varies_by) {
    const was = old[axis];
    const now = values[axis];
    if (!was || !now || was === now) continue;
    const at = name.toLowerCase().lastIndexOf(was.toLowerCase());
    if (at >= 0) name = name.slice(0, at) + now + name.slice(at + was.length);
  }
  return name === from.name
    ? `${from.name} (${group.varies_by
        .map((a) => values[a])
        .filter(Boolean)
        .join(" / ")})`
    : name;
}

// Making a group: names and values guessed from the parts' names ------------------------------------------------

const wordsOf = (s: string) => s.split(/\s+/).filter(Boolean);
const trimSeparators = (s: string) => s.replace(/^[\s\-–/·|,:]+|[\s\-–/·|,:]+$/g, "");

/** How many words every name starts with, and ends with (never overlapping), and each name's words. */
function sharedWords(items: readonly Pick<Item, "name">[]) {
  const lists = items.map((i) => wordsOf(i.name));
  const shortest = Math.min(...lists.map((l) => l.length));
  let head = 0;
  while (head < shortest && lists.every((l) => l[head] === lists[0][head])) head++;
  let tail = 0;
  while (tail < shortest - head && lists.every((l) => l[l.length - 1 - tail] === lists[0][lists[0].length - 1 - tail])) tail++;
  return { lists, head, tail };
}

/** A name for parts: the words all their names share ("GCU2 Joystick Cap Set", "GCU2 Torx … Screwdriver" → "GCU2 Torx Screwdriver"). */
export function commonName(items: readonly Pick<Item, "name">[]): string {
  if (items.length < 2) return items[0]?.name ?? "";
  const { lists, head, tail } = sharedWords(items);
  return trimSeparators([...lists[0].slice(0, head), ...lists[0].slice(lists[0].length - tail)].join(" ").replace(/\s+[-–/|]\s+(?=\S+$)/, " "));
}

/** Each part's values guessed from what its name adds to the others' ("… Set - Light Gray" → Light Gray; "2S / Ghost Black" split per axis). */
export function guessValues(items: readonly Pick<Item, "id" | "name" | "group_values">[], axes: readonly string[]): Record<string, GroupValues> {
  const { head, tail } = items.length > 1 ? sharedWords(items) : { head: 0, tail: 0 };
  const out: Record<string, GroupValues> = {};
  for (const i of items) {
    const had = valuesOf(i);
    const all = wordsOf(i.name);
    const rest = trimSeparators(all.slice(head, all.length - tail).join(" "));
    const parts = rest
      .split(/\s+[/|]\s+|\s+-\s+/)
      .map(trimSeparators)
      .filter(Boolean);
    out[i.id] = Object.fromEntries(axes.map((a, n) => [a, had[a] ?? (parts.length === axes.length ? parts[n] : n === axes.length - 1 ? rest : "")]));
  }
  return out;
}

// BOMs: what members are called there ---------------------------------------------------------------------

export type KnownAs = { value: string; option: string; family: string; bomId: string; lineId: string };

/** The option values each family's current revision maps to a part ("Ghost Black" on GC Ultimate 2), from bom_option_map. */
export function knownAs(ix: BomIndex, itemId: string): KnownAs[] {
  const out: KnownAs[] = [];
  const seen = new Set<string>();
  for (const key of ix.families.keys()) {
    const bom = currentRevision(ix, key);
    if (!bom) continue;
    const options = productOptions(variantsOf(ix, bom));
    for (const line of ix.linesByBom.get(bom.id) ?? []) {
      if (!line.option_name) continue;
      for (const [value, component] of ix.mapsByLine.get(line.id) ?? []) {
        if (component !== itemId) continue;
        const shown = options.find((o) => norm(o.name) === norm(line.option_name))?.values.find((v) => norm(v) === value) ?? value;
        const family = ownerLabel(ix, bom);
        const k = `${value}\u0001${family}`;
        if (seen.has(k)) continue;
        seen.add(k);
        out.push({ value: shown, option: line.option_name, family, bomId: bom.id, lineId: line.id });
      }
    }
  }
  return out;
}

/** "Ghost Black on GC Ultimate 2", several joined. */
export const knownAsText = (k: readonly KnownAs[]) => k.map((x) => `${x.value} on ${x.family}`).join(", ");

// BOMs: Fill from group ---------------------------------------------------------------------------------

/** The values of a line's option among the variants the line is in, in the product's order. */
export function lineValues(ix: BomIndex, bom: BomRow, line: BomLineRow): string[] {
  if (!line.option_name) return [];
  const variants = variantsOf(ix, bom).filter((v) => lineApplies(line, variantOptions(v)));
  const option = productOptions(variants).find((o) => norm(o.name) === norm(line.option_name));
  return option?.values ?? [];
}

export type FillSource = "value" | "kept" | "known" | "fallback" | "pick";
export const FILL_SOURCE_LABELS: Record<FillSource, string> = {
  value: "same value",
  kept: "as mapped now",
  known: "known as, on another BOM",
  fallback: "the line's part",
  pick: "pick one",
};
export type FillWrite =
  | { kind: "create"; value: string; itemId: string }
  | { kind: "update"; mapId: string; itemId: string }
  | { kind: "remove"; mapId: string };
export type FillRow = {
  value: string;
  /** What variants with this value get now (the map, else the line's part). */
  now: string | null;
  proposed: string | null;
  source: FillSource;
  /** For "pick": the members it could be. */
  candidates: string[];
  write: FillWrite | null;
};
export type OwnMap = { id: string; option_value: string; component_item_id: string };

/** The map row change that makes `value` get `itemId` on a line (null: nothing to write). Never a row equal to the line's part. */
export function fillWrite(value: string, itemId: string | null, fallback: string | null, own: OwnMap | undefined): FillWrite | null {
  if (!itemId) return null;
  if (itemId === fallback) return own && own.component_item_id !== itemId ? { kind: "remove", mapId: own.id } : null;
  if (!own) return { kind: "create", value, itemId };
  return own.component_item_id === itemId ? null : { kind: "update", mapId: own.id, itemId };
}

/**
 * What Fill from group proposes for an option line, value by value: a member whose value on the axis named like the
 * line's option is the same (its other axes pinned by the line's applies_when, else by what the line picks now); else
 * what the line maps it to now; else what other BOMs call that value (knownAs); else the line's own part; else "pick
 * one". A proposal equal to the line's part writes no map row, so a line that is "-LG for every colour but Ghost Black"
 * on purpose comes out unchanged and is never flagged.
 */
export function fillProposal(args: {
  line: Pick<BomLineRow, "option_name" | "applies_when" | "component_item_id">;
  values: readonly string[];
  group: Pick<GroupRow, "varies_by">;
  members: readonly MemberItem[];
  own: readonly OwnMap[];
  /** Other lines' maps of this option, value → parts (knownAs the other way round). */
  elsewhere?: ReadonlyMap<string, readonly string[]>;
}): { rows: FillRow[]; axis: string | null; pins: GroupValues } {
  const { line, values, group, own } = args;
  const current = args.members.filter(isCurrent);
  const ownBy = new Map(own.map((m) => [norm(m.option_value), m]));
  const fallback = line.component_item_id;
  const axis = group.varies_by.find((a) => norm(a) === norm(line.option_name)) ?? null;

  // The other axes: one value the line's conditions name, else the one every part it picks now has.
  const conditions = conditionsOf(line);
  const picked = new Set([...own.map((m) => m.component_item_id), ...(fallback ? [fallback] : [])]);
  const pickedMembers = args.members.filter((m) => picked.has(m.id));
  const pins: GroupValues = {};
  for (const a of group.varies_by) {
    if (a === axis) continue;
    const named = Object.entries(conditions).find(([k]) => norm(k) === norm(a))?.[1];
    if (named?.length === 1) {
      pins[a] = named[0];
      continue;
    }
    const seen = new Set(pickedMembers.map((m) => norm(valuesOf(m)[a])));
    if (seen.size === 1 && pickedMembers.length) pins[a] = valuesOf(pickedMembers[0])[a];
  }
  const pinned = current.filter((m) => Object.entries(pins).every(([a, v]) => norm(valuesOf(m)[a]) === norm(v)));
  const memberIds = new Set(args.members.map((m) => m.id));

  const rows = values.map((value): FillRow => {
    const o = ownBy.get(norm(value));
    const now = o?.component_item_id ?? fallback ?? null;
    const done = (proposed: string, source: FillSource): FillRow => ({
      value,
      now,
      proposed,
      source,
      candidates: [],
      write: fillWrite(value, proposed, fallback, o),
    });
    const byValue = axis ? pinned.filter((m) => norm(valuesOf(m)[axis]) === norm(value)) : [];
    if (byValue.length === 1) return done(byValue[0].id, "value");
    if (o) return done(o.component_item_id, "kept");
    const known = [...new Set(args.elsewhere?.get(norm(value)) ?? [])].filter((id) => memberIds.has(id) && pinned.some((m) => m.id === id));
    if (known.length === 1) return done(known[0], "known");
    if (fallback) return done(fallback, "fallback");
    return { value, now, proposed: null, source: "pick", candidates: (byValue.length ? byValue : pinned).map((m) => m.id), write: null };
  });
  return { rows, axis, pins };
}

/** Other lines' option maps for an option (same name, any family's current revision but this line), value → parts. */
export function mapsElsewhere(ix: BomIndex, option: string, exceptLineId: string): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const key of ix.families.keys()) {
    const bom = currentRevision(ix, key);
    for (const line of (bom && ix.linesByBom.get(bom.id)) ?? []) {
      if (line.id === exceptLineId || norm(line.option_name) !== norm(option)) continue;
      for (const [value, component] of ix.mapsByLine.get(line.id) ?? []) out.set(value, [...(out.get(value) ?? []), component]);
    }
  }
  return out;
}

/** The group an option line picks from: every part it picks (map rows and its own part) in one group. */
export function lineGroup(ix: BomIndex, line: BomLineRow, groupOf: (itemId: string) => string | null): string | null {
  const ids = [line.component_item_id, ...(ix.mapsByLine.get(line.id)?.values() ?? [])].filter((id): id is string => !!id);
  if (!ids.length) return null;
  const groups = new Set(ids.map(groupOf));
  return groups.size === 1 ? ([...groups][0] ?? null) : null;
}

// The Parts library's group row ---------------------------------------------------------------------------

/** "11 parts · Connection type × Color" */
export const groupSummary = (group: Pick<GroupRow, "varies_by">, members: readonly unknown[]) => `${plural(members.length, "part")} · ${axesText(group)}`;

// Files for every part ---------------------------------------------------------------------------------
// One PDF with a page per part (the sleeves' artwork as one export), or a file per part (Affinity's Export Slices, an
// artboard each), dropped on the group: each page or file is matched to the part it's for, which the person checks.

/** A page of the dropped PDF, or a dropped file: what it's called (file name, page label, bookmarks) and its words. */
export type FileCandidate = { name: string; text?: string };
/** How a part's page or file was found: its SKU, its values (the colour's words), or page order (nothing else matched). */
export type MatchBy = "sku" | "values" | "order";
export type FileMatch = { index: number | null; by: MatchBy | null };

const tokens = (s: string) => s.toLowerCase().match(/[\p{L}\p{N}]+/gu) ?? [];
/** Whether the words appear one after another (whole words: "Black" isn't in "Blackberry", SLV-130 isn't SLV-1301). */
const hasPhrase = (hay: readonly string[], words: readonly string[]) => words.length > 0 && hay.some((_, i) => words.every((w, j) => hay[i + j] === w));

/**
 * Which page or file each part gets (in `members`' order): the part's SKU on it beats all its values on it, which beat
 * some of them, and more words beat fewer ("Ghost Black" is the Ghost Black part's, not the Black one's). Between parts
 * a page fits as well, more of the part's name on it decides: a page saying "GC Ultimate 2S" and "Atomic Purple" is the
 * Wired Atomic Purple sleeve's ("GC Ultimate 2S / Atomic Purple"), though its values say "Wired". A page two parts still
 * want as much (or a part two pages fit as well) is left for the person to pick. When nothing matched and the
 * candidates are a PDF's pages, the pages go to the parts in order.
 */
export function matchFiles(
  group: Pick<GroupRow, "varies_by">,
  members: readonly Pick<Item, "sku" | "name" | "group_values">[],
  candidates: readonly FileCandidate[],
  pages: boolean,
): FileMatch[] {
  // Name and words with a break between, so a phrase doesn't run from one into the other.
  const hays = candidates.map((c) => [...tokens(c.name), "\u0000", ...tokens(c.text ?? "")]);
  const pairs: { m: number; c: number; score: number; by: MatchBy }[] = [];
  for (const [m, member] of members.entries()) {
    const sku = tokens(member.sku);
    const values = group.varies_by.map((a) => tokens(valuesOf(member)[a] ?? "")).filter((w) => w.length);
    const name = new Set(tokens(member.name));
    for (const [c, hay] of hays.entries()) {
      const found = values.filter((w) => hasPhrase(hay, w));
      const words = found.reduce((n, w) => n + w.length, 0);
      const named = Math.min(99, new Set(hay.filter((w) => name.has(w))).size); // only ever a tie-break
      if (hasPhrase(hay, sku)) pairs.push({ m, c, score: 30000 + sku.length * 100 + named, by: "sku" });
      else if (found.length) pairs.push({ m, c, score: (found.length === values.length ? 20000 : 10000) + words * 100 + named, by: "values" });
    }
  }
  pairs.sort((a, b) => b.score - a.score);
  const out: FileMatch[] = members.map(() => ({ index: null, by: null }));
  const takenM = new Set<number>();
  const takenC = new Set<number>();
  for (const p of pairs) {
    if (takenM.has(p.m) || takenC.has(p.c)) continue;
    const rival = (q: (typeof pairs)[number]) => q !== p && q.score === p.score && !takenM.has(q.m) && !takenC.has(q.c);
    const page = pairs.some((q) => rival(q) && q.c === p.c); // another part wants this page as much
    const part = pairs.some((q) => rival(q) && q.m === p.m); // another page fits this part as well
    if (page) takenC.add(p.c);
    if (part) takenM.add(p.m);
    if (page || part) continue;
    out[p.m] = { index: p.c, by: p.by };
    takenM.add(p.m);
    takenC.add(p.c);
  }
  if (pages && out.every((o) => o.index === null))
    return members.map((_, i) => (i < candidates.length ? { index: i, by: "order" } : { index: null, by: null }));
  return out;
}
